import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Breadcrumbs from "../../common/Breadcrumbs";
import { fetchOrdersSeller } from "../../api/orderFinalAPI";
import processingIcon from "../../assets/processing-icon.svg";
import readyToDispatchIcon from "../../assets/ready-to-dispatch.svg";
import dispatchedIcon from "../../assets/disptched.svg";
import deliveredIcon from "../../assets/delivered.svg";
import cancelIcon from "../../assets/cancel.svg";
import newIcon from "../../assets/new.svg";
import "./Orderdetails.css";
import DataTable from "../../common/DataTable";
import { useNavigate } from "react-router-dom";
import { SellerOrder, Pagination } from "../../types/Order";
import { getUserEmailFromLocalStorage } from "../../utils/localStorageUtils";
import SearchBar from "../../common/filters/SearchBar";

const orderStatusOptions = [
  { value: "New", label: "New", icon: newIcon },
  { value: "Order Approved", label: "Order Approved", icon: processingIcon },
  { value: "Processing", label: "Processing", icon: processingIcon },
  {
    value: "Ready for Dispatch",
    label: "Ready for Dispatch",
    icon: readyToDispatchIcon,
  },
  { value: "Dispatched", label: "Dispatched", icon: dispatchedIcon },
  { value: "Delivered", label: "Delivered", icon: deliveredIcon },

  { value: "Cancelled", label: "Cancelled", icon: cancelIcon },
];

const commonMenuItemStyles = {
  fontSize: "12px",
  display: "flex",
  gap: "8px",
  padding: "10px",
  margin: "14px 0px",
  borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
  fontFamily: "Poppins, sans-serif",
};

const OrderDetails = () => {
  const [orders, setOrders] = useState<SellerOrder[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [orderStatusFilter, setOrderStatusFilter] = useState("");
  const [filteredOrders, setFilteredOrders] = useState<SellerOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [pagination, setPagination] = useState<Pagination>();

  const orgId = localStorage.getItem("orgId");

  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      { label: "Customer", field: "customerName" },
      { label: "Location", field: "location" },
      { label: "Order Details", field: "orderDetails" },
      { label: "Payment", field: "paymentStatus" },
      {
        label: "Order Status",
        field: "orderStatus",
        cellStyle: { width: "15%" },
      },
    ],
    []
  );

  // Retrieve the email
  const emailId = getUserEmailFromLocalStorage() || "";
  console.log("Email retrieved from userRole key:", emailId);

  if (!emailId) {
    console.warn("No email ID found in local storage.");
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (!emailId) {
      navigate("/");
      return;
    }
    const loadOrders = async () => {
      try {
        const fetchedOrders = await fetchOrdersSeller({
          emailId,
          page,
          limit,
          search: debouncedSearchTerm,
        });
        setOrders(fetchedOrders.orders);
        setPagination(fetchedOrders.pagination);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [emailId, page, limit, debouncedSearchTerm]);

  const handleNavigation = useCallback(
    (id: string) => {
      navigate(`/${orgId}/seller/order-summary/${id}`);
    },
    [navigate]
  );

  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle filter change
  const handleFilterChange = (e: SelectChangeEvent<string>) => {
    setOrderStatusFilter(e.target.value);
  };

  // Handle sort order change
  const handleSortChange = (e: SelectChangeEvent<string>) => {
    setSortOrder(e.target.value);
  };

  const rowStyle = (index: number) => {
    return {
      height: "80px",
      backgroundColor:
        index % 2 === 0
          ? "rgba(71, 121, 204, 0.03)"
          : "rgba(100, 150, 255, 0.03)",
      cursor: "pointer",
    };
  };

  useEffect(() => {
    let filteredOrders = [...orders];

    if (sortOrder) {
      filteredOrders = filteredOrders.sort((a, b) => {
        const dateA = new Date(a.orderedDate);
        const dateB = new Date(b.orderedDate);
        return sortOrder === "newest"
          ? dateB.getTime() - dateA.getTime()
          : dateA.getTime() - dateB.getTime();
      });
    }

    // Apply search filter
    if (searchTerm) {
      filteredOrders = filteredOrders.filter((order) => {
        return (
          order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.paymentStatus
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          order.orderedDate.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.orderStatus.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.orderDetails?.orderId
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          order.orderDetails?.price
        );
      });
    }

    // Apply order status filter
    if (orderStatusFilter) {
      filteredOrders = filteredOrders.filter((order) =>
        order.orderStatus
          .toLowerCase()
          .includes(orderStatusFilter.toLowerCase())
      );
    }

    setFilteredOrders(filteredOrders);
  }, [searchTerm, orderStatusFilter, orders, sortOrder]);

  const orderTableData = useMemo(
    () =>
      filteredOrders.map((order) => ({
        _fullOrderData: order,
        customerName: (
          <Typography component="span" className="customer-name">
            {order.customerName}
          </Typography>
        ),
        location: (
          <Typography className="location" sx={{ fontSize: "12px" }}>
            {order.location}
          </Typography>
        ),
        orderDetails: (
          <Typography component="span" className="order-details">
            <span className="order-id">{order.orderDetails.orderId}</span>
            <span className="order-price">{order.orderDetails.price}</span>
          </Typography>
        ),
        paymentStatus: (
          <Typography className="payment-status" sx={{ fontSize: "12px" }}>
            {order.paymentStatus}
          </Typography>
        ),
        orderStatus: (
          <Typography className="order-status" sx={{ fontSize: "12px" }}>
            {order.orderStatus}
          </Typography>
        ),
      })),
    [filteredOrders, handleNavigation]
  );

  return (
    <>
      <Breadcrumbs
        paths={["Order Management", "All Orders"]}
        links={["/seller/orders", "/seller/orders"]}
      />

      {/* top filters conatiner */}

      <Box className="filters-section">
        {/* Search component */}
        <Box>
          <SearchBar handleSearch={handleSearch} searchTerm={searchTerm} />
        </Box>

        {/* filters conatiner */}

        <Box className="sort-filter-section">
          {/* sorting filter  */}
          <Box className="sorting-section">
            <InputLabel
              id="sort-by-label"
              sx={{
                marginRight: -1.1,
                color: "gray",
                fontSize: "12px",
                fontFamily: "Poppins, sans-serif",
                marginLeft: 0.5,
              }}
            >
              Sort by :
            </InputLabel>
            <Select
              id="sort-by-select"
              value={sortOrder}
              onChange={handleSortChange}
              IconComponent={KeyboardArrowDownIcon}
              role="combobox"
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "16px",
                    width: "100px",
                    marginTop: "10px",
                    gap: "8px",
                    fontFamily: "Poppins, sans-serif",
                  },
                },
              }}
              className="sorting-select"
              sx={{
                fontSize: "12px",
                fontWeight: "600",
                color: "rgba(61, 60, 66, 1)",
              }}
              labelId="sort-by-label"
            >
              <MenuItem
                value="newest"
                sx={{ fontSize: "12px", fontFamily: "Poppins, sans-serif" }}
              >
                Newest
              </MenuItem>
              <MenuItem
                data-testid="oldest"
                value="oldest"
                sx={{ fontSize: "12px", fontFamily: "Poppins, sans-serif" }}
              >
                Oldest
              </MenuItem>
            </Select>
          </Box>

          {/* filter component  */}
          <Box className="filter-section">
            {orderStatusFilter === "" && (
              <InputLabel
                id="order-status-label"
                className="MuiInputLabel-root"
              >
                Filters
              </InputLabel>
            )}
            <Select
              value={orderStatusFilter}
              onChange={handleFilterChange}
              renderValue={(selected) => {
                if (selected === "New") return "New";
                if (selected === "Order Approved") return "Order Approved";
                if (selected === "Processing") return "Processing";
                if (selected === "Ready for Dispatch")
                  return "Ready for Dispatch";
                if (selected === "Dispatched") return "Dispatched";
                if (selected === "Delivered") return "Delivered";

                if (selected === "Cancelled") return "Cancelled";
                return "";
              }}
              IconComponent={KeyboardArrowDownIcon}
              role="combobox"
              className="MuiSelect-outlined"
              id="order-status-select"
              labelId="order-status-label"
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "16px",
                    width: "160px",
                    marginTop: "5px",
                    gap: "8px",
                    fontFamily: "Poppins, sans-serif",
                  },
                },
              }}
            >
              {orderStatusOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  className="filter-menuItems"
                  sx={commonMenuItemStyles}
                >
                  <img src={option.icon} alt={option.value} />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      </Box>

      {/* order Table */}
      <Card sx={{ boxShadow: 3, paddingTop: 1 }}>
        <DataTable
          columns={columns.map((col) => ({
            ...col,
            align: "left",
            labelStyle: {
              fontSize: "18px",
              color: "rgba(21, 28, 40, 1)",
              fontWeight: "600",
              borderBottom: "none",
            },
          }))}
          data={orderTableData}
          emptyStateMessage="No Orders Available"
          rowStyle={rowStyle}
          onRowClick={(rowData) => {
            // Navigate using the full order data
            handleNavigation(rowData._fullOrderData.id);
          }}
        />
      </Card>
    </>
  );
};

export default OrderDetails;
